import { format } from 'date-fns';
import {
  X,
  Tag,
  Award,
  Zap,
  Store,
  Heart,
  Share2,
  MapPin,
  Calendar,
  Clock,
  AlertCircle,
  ShoppingCart,
  Info,
} from 'lucide-react';

interface Perk {
  id: string;
  title: string;
  merchant: string;
  discount: number;
  originalPrice: number | null;
  discountedPrice: number | null;
  currency: string;
  expiryDate: string;
  location: string;
  description: string;
  terms: string;
  isFlashDeal: boolean;
  isEmployerSponsored: boolean;
  imageUrl: string | null;
  imageAlt: string;
}

interface PerkDetailModalProps {
  perk: Perk;
  isOpen: boolean;
  onClose: () => void;
  isWishlisted: boolean;
  onToggleWishlist: () => void;
}

const PerkDetailModal = ({ perk, isOpen, onClose, isWishlisted, onToggleWishlist }: PerkDetailModalProps) => {
  if (!isOpen) return null;

  const daysUntilExpiry = Math.ceil(
    (new Date(perk.expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: perk.title,
        text: perk.description,
        url: window.location.href,
      }).catch((error) => console.log('Error sharing:', error));
    } else {
      // Fallback: Copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors duration-150"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>

          {/* Image Section */}
          <div className="relative h-64 bg-gradient-to-br from-primary/10 to-primary/5">
            {perk.imageUrl ? (
              <img
                src={perk.imageUrl}
                alt={perk.imageAlt || `${perk.title} detailed view`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Tag className="w-16 h-16 text-primary/20" />
              </div>
            )}

            {/* Badges */}
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              {perk.isEmployerSponsored && (
                <span className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded">
                  <Award className="w-3.5 h-3.5 mr-1" />
                  Employer Sponsored
                </span>
              )}
              {perk.isFlashDeal && (
                <span className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white text-sm font-semibold rounded animate-pulse">
                  <Zap className="w-3.5 h-3.5 mr-1" />
                  Flash Deal
                </span>
              )}
            </div>

            {/* Discount Badge */}
            <div className="absolute bottom-4 right-4 bg-green-600 text-white px-6 py-3 rounded-full font-bold text-2xl shadow-lg">
              {perk.discount}% OFF
            </div>
          </div>

          {/* Content Section */}
          <div className="p-6">
            {/* Merchant Info */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                  <Store className="w-6 h-6 text-gray-600" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">{perk.merchant}</h4>
                  <p className="text-xs text-gray-500">Verified Merchant</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={onToggleWishlist}
                  className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors duration-150"
                >
                  <Heart
                    className={`w-5 h-5 ${isWishlisted ? 'text-red-500 fill-current' : 'text-gray-600'}`}
                  />
                </button>
                <button
                  onClick={handleShare}
                  className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors duration-150"
                >
                  <Share2 className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{perk.title}</h2>

            {/* Key Details */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Price */}
              {perk.originalPrice && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Price</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg text-gray-500 line-through">
                      {perk.currency} {perk.originalPrice}
                    </span>
                    <span className="text-2xl font-bold text-green-600">
                      {perk.currency} {perk.discountedPrice}
                    </span>
                  </div>
                </div>
              )}

              {/* Location */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Location</div>
                <div className="flex items-center text-gray-900">
                  <MapPin className="w-4 h-4 mr-2" />
                  <span className="font-semibold">{perk.location}</span>
                </div>
              </div>

              {/* Valid Until */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Valid Until</div>
                <div className="flex items-center text-gray-900">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span className="font-semibold">
                    {format(new Date(perk.expiryDate), 'MMM dd, yyyy')}
                  </span>
                </div>
              </div>

              {/* Days Left */}
              <div className={`bg-gray-50 p-4 rounded-lg ${daysUntilExpiry <= 7 ? 'border-2 border-red-300' : ''}`}>
                <div className="text-sm text-gray-600 mb-1">Time Remaining</div>
                <div className={`flex items-center ${daysUntilExpiry <= 7 ? 'text-red-600' : 'text-gray-900'}`}>
                  <Clock className="w-4 h-4 mr-2" />
                  <span className="font-semibold">{daysUntilExpiry} days left</span>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
              <p className="text-gray-700 leading-relaxed">{perk.description}</p>
            </div>

            {/* Terms & Conditions */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-amber-900 mb-1">Terms & Conditions</h4>
                  <p className="text-sm text-amber-800">{perk.terms}</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                className="flex-1 px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-colors duration-200 flex items-center justify-center"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Claim This Perk
              </button>
              <button
                className="px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center"
              >
                <Info className="w-5 h-5 mr-2" />
                More Info
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerkDetailModal;
